import * as THREE from 'three';

export interface SliceResult {
    /** Intersection loop points in world space */
    points: THREE.Vector3[];
    /** Circumference length of the raw intersection in centimeters */
    length: number;
    /** Convex hull of intersection points (ordered) in world space */
    hull: THREE.Vector3[];
    /** Perimeter length of the convex hull in centimeters */
    hullLength: number;
}

// Convert mesh units to centimeters (adjust if needed)
const SCALE_FACTOR = 100;
export const get_vertexpos = function (v: number, geom: THREE.BufferGeometry): THREE.Vector3 {
    var vertexpos = geom.attributes.position
    const s = vertexpos.itemSize
    const x = v * s
    return new THREE.Vector3(vertexpos.array[x],
        vertexpos.array[x + 1], vertexpos.array[x + 2])
}
/**
 * Slice a mesh by a plane defined by an anchor vertex index and two sets of vertex indices for computing the plane normal.
 * @param mesh - THREE.Mesh to slice (BufferGeometry expected).
 * @param anchorIndex - Index of the vertex on the mesh to use as the slicing plane's anchor point.
 * @param normalSet1 - Array of vertex indices whose average position forms the first point for the plane normal.
 * @param normalSet2 - Array of vertex indices whose average position forms the second point for the plane normal.
 * @returns Array with a single SliceResult (loop closest to anchor), containing intersection points and circumference.
 */
export function sliceMesh(
    mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>,
    anchorIndex: number,
    normalSet1: number[],
    normalSet2: number[]
): SliceResult[] {
    // Ensure world transform is up-to-date
    mesh.updateMatrixWorld();

    const geom = mesh.geometry;
    const posAttr = geom.attributes.position as THREE.BufferAttribute;

    // Helper to resolve index accounting for indexed geometry
    const resolveIndex = (i: number) => geom.index ? geom.index.getX(i) : i;
   
    // Anchor point in world space
    const realAnchorIdx = resolveIndex(anchorIndex);
    const anchor = get_vertexpos(anchorIndex,geom).applyMatrix4(mesh.matrixWorld);


    // Compute the average world-space position for a set of indices
    const avgPosition = (indices: number[]) => {
        const sum = new THREE.Vector3();
        for (const idx of indices) {
            const v = get_vertexpos(idx,geom).applyMatrix4(mesh.matrixWorld);

            sum.add(v);
        }
        return sum.divideScalar(indices.length);
    };

    const p0 = avgPosition(normalSet1);
    const p1 = avgPosition(normalSet2);

    // Compute plane normal and define the plane
    const planeNormal = p1.clone().sub(p0).normalize().applyMatrix4(mesh.matrixWorld);;
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, anchor);

    // Prepare triangle data (non-indexed arrays for intersection)
    const positions = geom.index
        ? (geom.toNonIndexed().attributes.position.array as Float32Array)
        : (posAttr.array as Float32Array);

    const eps = 1e-9;
    const segments: [THREE.Vector3, THREE.Vector3][] = [];

    // Intersect each triangle with the plane
    for (let i = 0; i < positions.length; i += 9) {
        const [A, B, C] = [0, 3, 6].map(off =>
            new THREE.Vector3(
                positions[i + off],
                positions[i + off + 1],
                positions[i + off + 2]
            ).applyMatrix4(mesh.matrixWorld)
        );
        const dA = plane.distanceToPoint(A);
        const dB = plane.distanceToPoint(B);
        const dC = plane.distanceToPoint(C);

        let pts: THREE.Vector3[] = [];
        for (const [v1, d1, v2, d2] of [[A, dA, B, dB], [B, dB, C, dC], [C, dC, A, dA]] as const) {
            if (Math.abs(d1) < eps) pts.push(v1.clone());
            if (Math.abs(d2) < eps) pts.push(v2.clone());
            if (d1 * d2 < -eps * eps) {
                const t = d1 / (d1 - d2);
                pts.push(v1.clone().lerp(v2, t));
            }
        }
        // Remove duplicates
        pts = pts.filter((p, i, arr) =>
            arr.findIndex(q => q.distanceToSquared(p) < eps * eps) === i
        );
        if (pts.length >= 2) segments.push([pts[0], pts[1]]);
    }

    // Stitch segments into closed loops
    const loops: THREE.Vector3[][] = [];
    const used = segments.map(() => false);
    const same = (u: THREE.Vector3, v: THREE.Vector3) =>
        u.distanceToSquared(v) < eps * eps;

    for (let i = 0; i < segments.length; i++) {
        if (used[i]) continue;
        used[i] = true;
        const [start, end] = segments[i];
        const loop = [start.clone(), end.clone()];
        let closed = false;
        while (!closed) {
            let found = false;
            for (let j = 0; j < segments.length; j++) {
                if (used[j]) continue;
                const [U, V] = segments[j];
                if (same(loop[loop.length - 1], U)) {
                    loop.push(V.clone()); used[j] = true; found = true; break;
                }
                if (same(loop[loop.length - 1], V)) {
                    loop.push(U.clone()); used[j] = true; found = true; break;
                }
            }
            if (!found) break;
            if (same(loop[loop.length - 1], loop[0])) closed = true;
        }
        if (closed) {
            loop.push(loop[0].clone());
            loops.push(loop);
        }
    }

    // Compute circumference lengths
    // Compute raw intersection slice result
    const all: SliceResult[] = loops.map(loop => {
        // raw length calculation
        let L = 0;
        for (let k = 0; k < loop.length - 1; k++) {
            L += loop[k].distanceTo(loop[k + 1]);
        }
        const rawLength = L * SCALE_FACTOR;
        return { points: loop, length: rawLength, hull: [], hullLength: 0 };
    });

    // Select the slice closest to anchor
    const slice = all.length > 1
        ? all.reduce((best, s) => {
            // compute min distance to anchor
            const anchorDist = s.points.map(p => p.distanceTo(anchor)).reduce((min, d) => Math.min(min, d), Infinity);
            // min distance to best slice 
            const bestAvg = best.points.map(p=>p.distanceTo(anchor)).reduce((min, d) => Math.min(min, d), Infinity);
            return anchorDist < bestAvg ? s : best;
        }, all[0])
        : all[0];

    // Build 2D basis for plane
    const u = new THREE.Vector3();
    if (Math.abs(planeNormal.x) < 0.9) u.crossVectors(planeNormal, new THREE.Vector3(1, 0, 0));
    else u.crossVectors(planeNormal, new THREE.Vector3(0, 1, 0));
    u.normalize();
    const v = planeNormal.clone().cross(u).normalize();

    // Project points into plane coordinates
    type P2 = { x: number; y: number; orig: THREE.Vector3 };
    const pts2: P2[] = slice.points.map(p3 => {
        const vec = p3.clone().sub(anchor);
        return { x: vec.dot(u), y: vec.dot(v), orig: p3 };
    });

    // Compute 2D convex hull (Monotone chain)
    const hull2 = (() => {
        const sorted = pts2.slice().sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
        const cross = (o: P2, a: P2, b: P2) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
        const lower: P2[] = [];
        for (const p of sorted) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop(); lower.push(p); }
        const upper: P2[] = [];
        for (const p of sorted.slice().reverse()) { while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop(); upper.push(p); }
        upper.pop(); lower.pop();
        return lower.concat(upper);
    })();

    // Map hull back to 3D and compute perimeter
    const hull3 = hull2.map(h => h.orig);
    let HL = 0;
    for (let i = 0; i < hull3.length - 1; i++) {
        HL += hull3[i].distanceTo(hull3[i + 1]);
    }
    // close hull loop
    if (hull3.length > 1) HL += hull3[hull3.length - 1].distanceTo(hull3[0]);
    slice.hull = hull3;
    slice.hullLength = HL * SCALE_FACTOR;

    return [slice];
}

